<script lang="ts">
  import { onMount } from "svelte";
  import { track } from "../helpers/mixpanel";
  import type { IntegrationDetails, GetExternalContactsResponse } from "../helpers/types";

  import Management from "../components/integrations/Management.svelte";
  import Setup from "../components/integrations/Setup.svelte";
  import Home from "../components/integrations/Home.svelte";
  import EmptyState from "../components/integrations/EmptyState.svelte";
  import SquareManagement from "../components/integrations/SquareManagement.svelte";

  import {
    getIntegrations,
    connectNewIntegration,
    getExternalContacts,
  } from "../helpers/integrations-api";
  import { activeIntegrationsStore } from "../stores/integrations-store";
  import LoadingSpinner from "../components/LoadingSpinner.svelte";
  import { externalContactsStore } from "../stores/integrations-store";
  import { selectedBuyer } from "../stores/buyer";

  let newIntegration: IntegrationDetails | null;
  let selectedIntegration: IntegrationDetails | null;
  $: newIntegrationPlatformName = "";
  $: selectedIntegration = $selectedBuyer?.id ? null : null;
  $: newIntegration = $selectedBuyer?.id ? null : null;

  export let params: { selected?: string } = {};
  let activeIntegrations: IntegrationDetails[] = [];

  activeIntegrationsStore.subscribe((value) => {
    activeIntegrations = value;
  });

  $: if ($selectedBuyer?.id) {
    getIntegrations($selectedBuyer.id);
    activeIntegrations = [];
    selectedIntegration = null;
  }

  // Handle menu route change if currently on integrations page already
  $: if (params.selected) {
    handlePlatformSelect(params.selected, undefined);
  }

  const handlePlatformSelect = async (
    platformName: string,
    integration: IntegrationDetails | undefined
  ) => {
    if (!platformName) {
      newIntegration = null;
      selectedIntegration = null;
      getIntegrations($selectedBuyer?.id || "");
      return;
    }
    selectedIntegration = null;
    newIntegration = null;
    externalContactsStore.set(null);
    getIntegrations($selectedBuyer?.id || "");
    newIntegrationPlatformName = platformName;

    if (!integration || integration.platform !== platformName) {
      const newIntegrationResponse = await connectNewIntegration(
        platformName,
        $selectedBuyer?.id || ""
      );
      //create new
      newIntegration = newIntegrationResponse[0];
    } else {
      selectedIntegration = integration;
    }

    getExternalContacts(selectedIntegration?.id || newIntegration?.id || "");
  };

  const closeIntegration = () => {
    selectedIntegration = null;
    newIntegration = null;
  };

  onMount(async () => {
    if (params.selected) {
      handlePlatformSelect(params.selected, undefined);
    }

    track("integrations_management_open", {
      buyer_id: $selectedBuyer?.id,
      buyer_name: $selectedBuyer?.name,
    });
  });

  let externalContacts: GetExternalContactsResponse | null;

  externalContactsStore.subscribe((value) => {
    externalContacts = value;
  });
</script>

<div class="container">
  {#if $selectedBuyer}
    <div class="content">
      {#if !activeIntegrations}
        <div class="loading-container">
          <div class="loading-spinner">
            <LoadingSpinner size={50} />
          </div>
        </div>
      {:else}
        {#if !selectedIntegration && !newIntegration}
          <Home
            selectedIntegration={selectedIntegration || newIntegration}
            platformSelected={(platform, integration) =>
              handlePlatformSelect(platform, integration)}
          />
        {/if}
        <div class="settings-panel">
          {#if selectedIntegration}
            {#if selectedIntegration.platform === "Square"}
              <SquareManagement
                integration={selectedIntegration}
                disconnected={(platform) => handlePlatformSelect(platform, undefined)}
                onClose={closeIntegration}
              />
            {:else}
              <Management
                integration={selectedIntegration}
                disconnected={(platform) => handlePlatformSelect(platform, undefined)}
                onClose={closeIntegration}
              />
            {/if}
          {:else if newIntegration}
            <Setup integration={selectedIntegration || newIntegration} onClose={closeIntegration} />
          {:else}
            <EmptyState integrations={activeIntegrations} />
          {/if}
        </div>
      {/if}
    </div>
  {/if}
</div>

<style>
  .loading-container {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .container {
    flex: 1;
    display: flex;
    flex-direction: column;
    background-color: white;
    height: 100vh;
    height: 100%;
  }

  .content {
    display: flex;
    flex-direction: row;
    height: 100%;
  }

  .settings-panel {
    height: 100%;
    width: 100%;
    overflow-y: scroll;
    background-color: var(--rekki-neutral-01);
    min-width: 840px;
  }

  .loading-container {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 75vh;
    width: 100%;
  }
</style>
